import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import LoginForm from './LoginForm';
import RegisterForm from './RegisterForm';
import ResetPasswordFlow from './ResetPasswordFlow';
import BusyOverlay from '../../shared/components/BusyOverlay/BusyOverlay';
import useAuthView, { VIEW_LOGIN, VIEW_REGISTER, VIEW_RESET } from '../hooks/useAuthView';
import { resolveReturnUrl } from '@common/helpers/loginRedirect';

// Redirect target after successful authentication. Kept configurable via
// the root element's data-redirect attribute so host pages (tutoriage,
// bidforwriting, essaydaddy) can point to their own brand landing.
const DEFAULT_REDIRECT = '/account/';

// Animated decorative hero-art — shield + checkmark with a pulsing
// radial halo. "Your account is safe" metaphor — reads well next to
// login / sign-up copy. Shield body paints `fill="currentColor"`, so
// the brand `--bidding-color-accent` set on `.bidding-auth2-side__shield`
// in SCSS drives the colour (bidforwriting → mint, tutoriage → cyan).
function AuthSideArt() {
    return (
        <div className="bidding-auth2-side__art" aria-hidden="true">
            <span className="bidding-auth2-side__shield-pulse" />
            <svg viewBox="0 0 120 140" className="bidding-auth2-side__shield">
                <path
                    d="M60 8 L110 26 V70 Q110 108 60 132 Q10 108 10 70 V26 Z"
                    fill="currentColor"
                />
                <path
                    d="M42 68 L56 82 L82 56"
                    fill="none"
                    stroke="#fff"
                    strokeWidth="6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        </div>
    );
}

export default function AuthPanel({ redirectTo }) {
    // returnUrl is read straight off the URL (`?returnUrl=`) via the shared
    // helper — the single contract with the server builder
    // (`Request::redirectToLoginPage()`) and the 401 handlers. No data-attr /
    // session side-channel; the helper also enforces the open-redirect allowlist.
    const [view, setView] = useAuthView();
    const [busy, setBusy] = useState(null);
    const authorized = useSelector(state => state.user.authorized);
    const returnUrl = resolveReturnUrl();

    useEffect(() => {
        if (authorized !== true) return undefined;

        setBusy('Signing you in…');
        const t = setTimeout(() => {
            window.location.href = returnUrl || redirectTo || DEFAULT_REDIRECT;
        }, 650);
        return () => clearTimeout(t);
    }, [authorized, redirectTo, returnUrl]);

    const onSuccess = (message) => {
        setBusy(message || 'Signing you in…');
    };

    const isLogin = view === VIEW_LOGIN;
    const isRegister = view === VIEW_REGISTER;
    const isReset = view === VIEW_RESET;

    let content;
    if (isRegister) {
        content = (
            <RegisterForm
                onLogin={() => setView(VIEW_LOGIN)}
                onSuccess={() => onSuccess('Creating your account…')}
                onBusy={setBusy}
                busyMessage="Creating your account…"
            />
        );
    } else if (isReset) {
        content = <ResetPasswordFlow onBack={() => setView(VIEW_LOGIN)} />;
    } else {
        content = (
            <LoginForm
                onForgot={() => setView(VIEW_RESET)}
                onRegister={() => setView(VIEW_REGISTER)}
                onSuccess={() => onSuccess('Signing you in…')}
                onBusy={setBusy}
                busyMessage="Signing you in…"
            />
        );
    }

    return (
        <div className="bidding-auth2-shell">
            <header className="bidding-auth2-header">
                <a
                    href="/"
                    className="bidding-auth2-header__logo"
                    aria-label="Home"
                >
                    Tutoriage
                </a>
                {!isReset && (
                    <div className="bidding-auth2-header__switch" role="tablist">
                        <button
                            type="button"
                            role="tab"
                            aria-selected={isLogin}
                            className={`bidding-auth2-header__tab${isLogin ? ' is-active' : ''}`}
                            onClick={() => setView(VIEW_LOGIN)}
                        >
                            Log in
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={isRegister}
                            className={`bidding-auth2-header__tab${isRegister ? ' is-active' : ''}`}
                            onClick={() => setView(VIEW_REGISTER)}
                        >
                            Sign up
                        </button>
                    </div>
                )}
            </header>

            <main className="bidding-auth2-stage">
                <div className="bidding-auth2-stage__inner">
                    {/* Decorative copy + art on the left @ ≥1024px. Reads
                        like a lightweight brand moment — "why you'd sign in"
                        — without taking attention from the form. */}
                    <aside className="bidding-auth2-side">
                        <span className="bidding-auth2-side__kicker">Welcome back</span>
                        <h1 className="bidding-auth2-side__title">
                            Writers are a click away.
                        </h1>
                        <p className="bidding-auth2-side__text">
                            Sign in to post an order, chat with writers, and track the
                            progress, all in one place. New here? <strong>Sign up</strong>{' '}
                            is one step away.
                        </p>
                        <AuthSideArt />
                    </aside>

                    {content}
                </div>
            </main>

            {busy && <BusyOverlay message={busy} />}
        </div>
    );
}
